import { isAllowedEvent, type TrackedEvent } from '../analytics-events';

interface TrackContext {
  appId: string;
  appSlug: string;
}

/**
 * Returns a `track(event, props)` function exposed to bundles via
 * `useHook().track`. Validates `event` against the central allowlist
 * (`analytics-events.ts`) before forwarding to `window.posthog.capture`.
 *
 * Always injects `app_id` and `app_slug` from the SDK's HookConfig so every
 * tracked event is queryable by tenant in Studio (`properties.app_id`),
 * regardless of whether the caller remembered to pass it. Caller-supplied
 * `props` win on key collision so apps can still override if needed.
 *
 * Behavior:
 * - Allowlist hit + `window.posthog.capture` available → forward.
 * - Allowlist miss → `console.warn` (dev) + drop. Never throws.
 * - `window.posthog` absent → silent no-op (PostHog not initialized yet
 *   or env vars missing — see `frontend/shell/src/observability/posthog.ts`).
 * - `capture` itself throws → swallow.
 *
 * Why allowlist: prevents bundles (third-party code) from emitting arbitrary
 * events that inflate PostHog costs. See ADR-011 + ADR-019.
 *
 * Why auto-injection of app_id: Studio's HogQL queries filter by
 * `properties.app_id`. Without auto-injection, every helper hook that calls
 * track() must remember to pass appId — easy to forget, silently breaks the
 * dropoff dashboard. See docs/insights/2026-04-30-hogql-group0-vs-app-id.md
 * for the related $group_0 trap.
 */
export function createTrack(ctx?: TrackContext) {
  return (event: TrackedEvent | (string & {}), props?: Record<string, unknown>): void => {
    if (!isAllowedEvent(event)) {
      // Dev warn helps catch typos and unsanctioned events. Prod: silent drop —
      // analytics is never on the critical path of an app.
      // eslint-disable-next-line no-console
      console.warn(`[hook-sdk] track('${event}') rejected: not in ALLOWED_EVENTS`);
      return;
    }
    if (typeof window === 'undefined') return;
    const ph = (window as unknown as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    if (typeof ph?.capture !== 'function') return;
    try {
      const merged = ctx
        ? { app_id: ctx.appId, app_slug: ctx.appSlug, ...(props ?? {}) }
        : (props ?? {});
      ph.capture(event, merged);
    } catch {
      /* noop — analytics never breaks the app */
    }
  };
}
